import type { Branch } from './order.type'
import type { FreeNote } from './free-note.type'
import type { StateProvinceAll } from './state-province.type'
import type { DayOfWeek } from './order-ticket.type'
import type { PaginationResponse } from './pagination.type'

export type ExternalCustomer = {
  id: number
  account_number: number
  account_name: string
  party_name: string
  salesrep_number: number // 営業担当者番号
  salesrep_name: string // 営業担当者名
}

export type ExternalCustomerItem = {
  id: number
  customer_user_info_id: number
  item_num: string // int parseできそう
  default_uom: string
  item_group_id: number | null
  add_item_flag: number | null
  new_end_date: string | null
  last_ordered_quantity: number | null
  last_order_quantity_uom: string | null
  last_shipped_date: string | null
  display_fields: number
  hide_flag: number
  item_name: string
  item_status: string
  pack_size: string | null
  primary_uom_code: string
  secondary_uom_code: string | null
  conversion_rate: string | null
  primary_uom_flag: number
  min_qty_order: string | null
  liquor: boolean | null
  org_id: number
}

export type CustomerListResponse = {
  customers: ExternalCustomer[]
}
export type CustomerItemListResponse = {
  items: ExternalCustomerItem[]
}

// ここから下は、分析用にsyncして格納してあるデータ

export type SalesRep = {
  salesrep_number: number
  salesrep_name: string
  createdAt: Date
  updatedAt: Date
}

// 営業として顧客をどう扱うかの区分。upstream (wismettac-sales-ai-api) の
// src/customers/types/customer-target-type.type.ts をミラーしたもの

// DB 保存用の全集合。response（Customer.customerTargetType）はこちらを使う。
export const CustomerTargetTypeAll = {
  Watch: 'watch', // 重点的に追う
  Neutral: 'neutral', // どちらでもない（default）
  Park: 'park', // 当面アプローチを寝かせる
} as const

export type CustomerTargetTypeAll =
  (typeof CustomerTargetTypeAll)[keyof typeof CustomerTargetTypeAll]

// 現役仕様。update payload など送信側はこちらを使う。
export const CustomerTargetType = {
  Watch: CustomerTargetTypeAll.Watch,
  Neutral: CustomerTargetTypeAll.Neutral,
  Park: CustomerTargetTypeAll.Park,
} as const

export type CustomerTargetType = (typeof CustomerTargetType)[keyof typeof CustomerTargetType]

// GET /customers の絞り込み query param。all は絞り込みなし（API default）
export const CustomerTargetQuery = {
  all: 'all',
  ...CustomerTargetType,
} as const

export type CustomerTargetQuery = (typeof CustomerTargetQuery)[keyof typeof CustomerTargetQuery]

export const CustomerTargetTypeLabel: Record<CustomerTargetTypeAll, string> = {
  watch: 'Watch',
  neutral: 'Neutral',
  park: 'Park',
}

// GET /customers の営業中 / 閉店絞り込み query param。上流 (wismettac-sales-ai-api) の
// src/customers/types/customer-open-status.type.ts をミラーしたもの。all は絞り込みなし（API default）
export const CustomerOpenStatusQuery = {
  all: 'all',
  open: 'open',
  closed: 'closed',
} as const

export type CustomerOpenStatusQuery =
  (typeof CustomerOpenStatusQuery)[keyof typeof CustomerOpenStatusQuery]

export const CustomerOpenStatusQueryLabel: Record<CustomerOpenStatusQuery, string> = {
  all: 'All',
  open: 'Open',
  closed: 'Closed',
}

export type Customer = {
  id: number
  branchOrgId: number
  branch: Branch
  party_name: string
  account_number: number
  account_name: string
  salesrep_number: number
  salesrep: SalesRep
  city: string // self-managed（readonly）
  state: StateProvinceAll | null // self-managed（readonly）
  customerTargetType: CustomerTargetTypeAll // self-managed. 営業としての扱いの区分
  enabledAutoHeadsup: boolean // self-managed. daily routine の自動 headsup 調査対象か
  closedAt: Date | null // self-managed. 閉店日時（null = 営業中）。WeConnect 同期では上書きされない
  // self-managed. 顧客ごとの注力方針。summary は一行要約、description は補足。
  // 変更は CustomerPriorityChangeLog に記録される
  prioritySummary: string
  priorityDescription: string
  profile: CustomerProfile | null // self-managed. 発注運用情報（初回編集まで不在）
  createdAt: Date
  updatedAt: Date
}

// 他リソースの relation として customer を読んだときの形
export type CustomerWithoutRelations = Omit<Customer, 'branch' | 'salesrep' | 'profile'>

// Customer Action 画面のタブ。URL の tab query で開くタブを指定する
export const CustomerActionTab = {
  matrix: 'matrix',
  deals: 'deals',
  orders: 'orders',
  notes: 'notes',
  profile: 'profile',
  strategy: 'strategy',
} as const
export type CustomerActionTab = keyof typeof CustomerActionTab

export const parseCustomerActionTab = (value: unknown): CustomerActionTab =>
  Object.values(CustomerActionTab).find((tab) => tab === value) ?? CustomerActionTab.matrix

// 発注の繰り返し間隔。上流 (wismettac-sales-ai-api) の
// src/customers/types/order-frequency.type.ts をミラーしたもの
export const OrderFrequency = {
  Weekly: 'Weekly',
  Biweekly: 'Biweekly',
  Monthly: 'Monthly',
  EventOnly: 'EventOnly',
  Irregular: 'Irregular',
} as const
export type OrderFrequency = (typeof OrderFrequency)[keyof typeof OrderFrequency]

// スケジュール一覧の絞り込み対象サイクル（query param）。
// 上流 (wismettac-sales-ai-api) src/customers/dto/customer-schedule.dto.ts をミラー
export const OrderCycle = {
  Order: 'order',
  Delivery: 'delivery',
} as const
export type OrderCycle = (typeof OrderCycle)[keyof typeof OrderCycle]

// Customer と 1:1。発注に関する運用情報（旧: excel 管理）。初回編集まで不在
export type CustomerProfile = {
  customerId: number
  orderFrequency: OrderFrequency // 繰り返し間隔
  deliveryDays: DayOfWeek[] // 配送される曜日
  orderDays: DayOfWeek[] // 発注作業を行う曜日。TODO: 一部の几帳面ユーザだけが登録している（それ以外は一応 Delivery の前日で入れている）。実際はこちらが連絡する日みたいな意味合いなのでいずれ別の管理方法に移行予定
  isPassive: boolean // こちらから Order Taking のアクションを起こす必要がない（発注が勝手に来る）
  orderTimeMemo: string // 発注作業を行う時刻の自由記述（例: Tue/Wed/Thu 11AM, Fri 2PM）
  orderMethod: string
  orderPersonName: string
  orderPersonLanguage: string
  contactInfo: string
  contactMemo: string
  deliveryMemo: string
  paymentMethod: string // OPS / CHECK など。TODO: AR 系機能で吸収できそう
  orderMemo: string // 発注時の注意事項や毎回使う Deal など。TODO: salesrep task か headsup で吸収できそう
  createdAt: Date
  updatedAt: Date
}

// PATCH /customers/:id/profile の body。全カラムを送る（無ければ作成）
export type UpdateCustomerProfilePayload = Omit<
  CustomerProfile,
  'customerId' | 'createdAt' | 'updatedAt'
>

// PATCH /customers/:id の body。self-managed の customerTargetType を更新
export type UpdateCustomerPayload = { customerTargetType: CustomerTargetType }

// PATCH /customers/:id の body。self-managed の自動 headsup 調査フラグを更新
export type UpdateCustomerAutoHeadsupPayload = Pick<Customer, 'enabledAutoHeadsup'>

// PATCH /customers/:id の body。self-managed の注力方針を更新。
// 渡した値でそのまま上書きされるため、据え置く項目も現在値を送る
export type UpdateCustomerPriorityPayload = Pick<
  Customer,
  'prioritySummary' | 'priorityDescription'
>

// ERP の支払条件名（customer_bill_to.payment_term）を解析した種類。Net は日数ごとに別の値。
// 上流 (wismettac-sales-ai-api) の src/customers/types/payment-term-type.type.ts をミラーしたもの

// DB 保存用の全集合。response（CustomerArBillTo.payment_term_type）はこちらを使う
export const PaymentTermTypeAll = {
  Net7: 'Net7',
  Net14: 'Net14',
  Net20: 'Net20',
  Net30: 'Net30',
  Net35: 'Net35',
  Net45: 'Net45',
  Net60: 'Net60',
  Net75: 'Net75',
  Cod: 'Cod', // 代金引換
  Cia: 'Cia', // 前払い
  Immediate: 'Immediate', // 請求後すぐ支払い
  Unknown: 'Unknown', // 支払条件名が空、または解析できない書式
} as const
export type PaymentTermTypeAll = (typeof PaymentTermTypeAll)[keyof typeof PaymentTermTypeAll]

// 現役仕様。query など送信側はこちらを使う
export const PaymentTermType = {
  Net7: PaymentTermTypeAll.Net7,
  Net14: PaymentTermTypeAll.Net14,
  Net20: PaymentTermTypeAll.Net20,
  Net30: PaymentTermTypeAll.Net30,
  Net35: PaymentTermTypeAll.Net35,
  Net45: PaymentTermTypeAll.Net45,
  Net60: PaymentTermTypeAll.Net60,
  Net75: PaymentTermTypeAll.Net75,
  Cod: PaymentTermTypeAll.Cod,
  Cia: PaymentTermTypeAll.Cia,
  Immediate: PaymentTermTypeAll.Immediate,
  Unknown: PaymentTermTypeAll.Unknown,
} as const
export type PaymentTermType = (typeof PaymentTermType)[keyof typeof PaymentTermType]

export const PaymentTermTypeLabel: Record<PaymentTermTypeAll, string> = {
  Net7: 'Net 7',
  Net14: 'Net 14',
  Net20: 'Net 20',
  Net30: 'Net 30',
  Net35: 'Net 35',
  Net45: 'Net 45',
  Net60: 'Net 60',
  Net75: 'Net 75',
  Cod: 'COD',
  Cia: 'CIA',
  Immediate: 'Immediate',
  Unknown: 'Unknown',
}

export type CustomerArBillTo = {
  location_code: string
  payment_term: string
  payment_term_type: PaymentTermTypeAll
  early_payment_discount_percent: number | null // null は割引なし。payment_term_type が Unknown のときも null
  early_payment_discount_days: number | null // 請求から何日以内に払えば割引になるか
}

// 顧客の AR 情報。値はすべて日次 sync 由来で lastSyncedAt 時点のもの
export type CustomerAr = {
  credit_limit: string | null // PostgreSQL decimal type returns as string
  balance: string | null
  site_hold_status: boolean // Oracle 上の hard hold
  isOverCreditLimit: boolean // balance > credit_limit の推定（soft hold 相当）。どちらか欠けると false
  foodBillTos: CustomerArBillTo[]
  liquorBillTos: CustomerArBillTo[]
  lastSyncedAt: Date | null // 記録開始後に一度も取得していなければ null
}

export type CustomerDetail = Customer & {
  pinnedFreeNoteId: number | null
  pinnedFreeNote: FreeNote | null
  ar: CustomerAr
}

export type GetCustomersQuery = {
  page: number
  limit: number
  branchOrgId?: number
  salesrep_number?: number
  customerTargetType?: CustomerTargetQuery
  blankScheduleOnly?: boolean // 発注・配送曜日がどちらも未設定（profile 不在含む）の顧客に絞る
  openStatus?: CustomerOpenStatusQuery
}

export type GetSalesrepsQuery = {
  page: number
  limit: number
}

// GET /customers/schedules の 1 件。Customer / CustomerProfile から必要列を引き写す
export type CustomerSchedule = Pick<
  Customer,
  'id' | 'account_number' | 'account_name' | 'salesrep_number'
> &
  Pick<CustomerProfile, 'orderFrequency' | 'orderDays' | 'deliveryDays' | 'orderTimeMemo'> & {
    lastOrderedDate: Date | null // 直近注文日。注文実績が無ければ null
  }

export type GetCustomerSchedulesQuery = {
  salesrepNumber: number
  cycleOf: OrderCycle
  dayOfWeek: DayOfWeek
  page: number
  limit: number
}

export type CustomerScheduleListResponse = PaginationResponse<CustomerSchedule>

// GET /customers/ar の 1 件
export type CustomerArListItem = Pick<Customer, 'id' | 'account_number' | 'account_name'> & {
  ar: CustomerAr
}

// 上流の src/customers/dto/customer-ar.dto.ts の FindCustomerArQueryDto をミラーしたもの。
// 絞り込みは顧客単位で、一致した顧客の bill_to は Food / Liquor とも全件返る
export type GetCustomerArQuery = {
  salesrepNumber: number
  siteHoldOnly: boolean // Site hold（hard hold）中の顧客に絞る
  // balance > credit_limit の顧客と、COD の bill_to を持ち balance > 0 の顧客に絞る。balance が null の顧客は含まない
  hasCreditRiskOnly: boolean
  hasLiquor: boolean // Liquor の bill_to を持つ顧客に絞る
  paymentTermTypes: PaymentTermType[] // いずれかの bill_to が一致する顧客に絞る。[] は絞り込みなし
  page: number
  limit: number
}

export type CustomerArListResponse = PaginationResponse<CustomerArListItem>
